import { HostListener, Input, ViewContainerRef, Directive } from '@angular/core';
import { ComponentType, SohoModalDialogRef, SohoModalDialogService } from 'ids-enterprise-ng';
import { LMDirtyCheckDialogComponent } from '@modal/lm-dirty-check-dialog.component';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMHtmlPageUtilService } from '@html/lm-html-page-util.service';
import { ListResource } from '@resources/list.resource';
import { BehaviorSubject, Observable, Observer } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LMContextService } from '@services/lm-context.service';
import { LMDialogButtonKey, LMFormType, LMUniqueIdService } from '@utils/index';
import { LMPendingChangesDialogComponent } from '@modal/lm-pending-changes-dialog.component';
import { LMComponentInfoService } from '@services/lm-componentinfo.service';
import { LMCtrlShiftClickEvent } from './lm-ctrl-shift-click-event';
import { LMAppComponentService } from '@utils/lm-app-component.service';

export interface LMDialogInfo {
  name?: string;
  message?: string
}

@Directive()
export abstract class LMBaseComponent {

  protected dialog: SohoModalDialogRef<any>;
  private baseCompId: string;

  private m_componentDestroyed = false;

  @HostListener('click', [ '$event' ]) onClick(event) {
    if ((this.lmComponentInfoService.getCardviewSpec() || (event.ctrlKey || event.metaKey) && event.shiftKey)) {
      // if we are on a field and we have click bweyond the bounds of that field stop this field info proccessing
      // and allow the event to bubble to the form.
      const element = event.currentTarget;
      if (element) {
        const fieldElements = Array.prototype.filter.call(element.children, function(kids) {
          return kids.matches('div[lm-field-options]');
        });
        const fieldElement = fieldElements.length > 0 ? fieldElements[0] : undefined;

        // check to see if this is field and that it is clicking beyond the field on the form itself
        if (fieldElement) {
          const rect = fieldElement.getBoundingClientRect();
          const right = rect.right;
          if (fieldElement && event.x > right) {
            return;
          }
        }
      }

      event.stopPropagation();
      this.displayComponentInfo(event, this);
    }
  }

  @Input() placeHolder: ViewContainerRef;

  protected constructor(
    protected sohoModalDialogService: SohoModalDialogService,
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    protected lmComponentInfoService: LMComponentInfoService,
    protected lmUniqueIdService: LMUniqueIdService,
    protected lmAppComponentService: LMAppComponentService,
  ) {
  }

  public legacyWebUI = false;
  public url3_5 = undefined;
  public unsupportedPattern = false;
  private userWebRequestId = '';

  public uiCompare = false;

  public onAppMenuTriggerEvent(event: MouseEvent) {
    event.stopImmediatePropagation();
    this.lmAppComponentService.getAppComponent().toggleMenu();
    this.lmAppComponentService.getAppComponent().onMenuSearchCleared();   // onAppMenuTriggerEvent(event);
  }

  public abstract getComponentInfo(additionInfo?: LMCompInfoAdditionalInfo): LMComponentInfo;

  public abstract getComponentPlaceHolder(): ViewContainerRef;
  public abstract getUserWebRequestId(): string;

  public displayComponentInfo(event: LMCtrlShiftClickEvent, component?: LMBaseComponent): void {

    if ((!((event.ctrlKey || event.metaKey) && event.shiftKey)) && !event.isPage && !this.lmComponentInfoService.getCardviewSpec()) {
      return;
    }

    const placeHolder = this.getComponentPlaceHolder();
    const compInfo: LMComponentInfo = this.getComponentInfo(event.additionInfo);

    // allow fields to continue toward display. Block forms when the Comp type is List
    const typeFromEvent: string = this.lmComponentInfoService.getComponentTypeFromEvent(event);
    if (!(compInfo.componentType === 'FIELD' && compInfo.specField === 'NONSPECFIELD')
    && (typeFromEvent !== null && ((compInfo.componentType === 'FORM' && typeFromEvent.indexOf('FORM') < 0 && compInfo.type !== 'Search'))
    && compInfo.componentType !== typeFromEvent)) {
      return;
    }

    compInfo.isPage = (compInfo.componentType !== 'FIELD' && (this.lmComponentInfoService.closest(event.srcElement, 'lm-page')));
    compInfo.routerComponent = component;
    this.lmComponentInfoService.displayComponentInfo(event, placeHolder, compInfo);
    // this.displayDialog(placeHolder, LMComponentInfoComponent, buttons, compInfo, 'Component Info');
  }

  public allowPageNavigation(): Promise<boolean> | boolean {
    return true;
  }

  public getBaseCompId() {
    if (!this.baseCompId) {
      this.baseCompId = 'WRID-' + this.lmUniqueIdService.getCompIndex();
    }
    return this.baseCompId
  }

  public getRouteCompUserWebRequestId(): string {
    const appComp = this.lmAppComponentService.getAppComponent();
    if (!appComp) {
      return Soho.Locale.translate('NoComp');
    }
    const routeComp = appComp.getRouteComponent();
    if (!routeComp) {
      return Soho.Locale.translate('NoRouteComp');
    }

    return routeComp.getUserWebRequestId();
  }

  public showUnsavedChanges(info: LMDialogInfo): Observable<UnsavedChangesResponse> {

    return new Observable<UnsavedChangesResponse>(observer => {
      const dialogId = 'UnsavedChanges';

      const buttons: SohoModalButton[] = [
        {
          id:    this.lmUniqueIdService.uniqueId([dialogId, LMDialogButtonKey.NO]),
          text:  Soho.Locale.translate(LMDialogButtonKey.NO),
          click: (e, modal) => {
            observer.next(LMDialogButtonKey.NO);
            observer.complete();
            modal.close();
            this.hideBusyIndicator();
          },
        },
        {
          id:    this.lmUniqueIdService.uniqueId([dialogId, LMDialogButtonKey.YES]),
          text:  Soho.Locale.translate(LMDialogButtonKey.YES),
          click: (e, modal) => {
            observer.next(LMDialogButtonKey.YES);
            observer.complete();
            modal.close();
          },
          isDefault: true
        }
      ];

      this.displayUnsavedChangesDialog(LMDirtyCheckDialogComponent, buttons, info, Soho.Locale.translate('UnsavedChangesMessage'));
    });
  }

  public displayUnsavedChanges(): Promise<boolean> {

    const youMayNavigateSource = new BehaviorSubject<boolean>(false);
    const youMayNavigate: Observable<boolean> = youMayNavigateSource.asObservable();
    const info: LMDialogInfo = { message: Soho.Locale.translate('DocumentHasChangesMessage') };  // The document has changes and will be lost if you leave the current document

    const dialogId = 'UnsavedChanges';
    const buttons: SohoModalButton[] = [
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.NO ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.NO),
        click: (e, modal) => {
          youMayNavigateSource.next(true);
          youMayNavigateSource.complete();
          modal.close();
        },
      },
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.YES ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.YES),
        click: (e, modal) => {
          youMayNavigateSource.next(false);
          youMayNavigateSource.complete();
          modal.close();
          this.hideBusyIndicator();
        },
        isDefault: true
      }
    ];

    this.displayUnsavedChangesDialog(LMDirtyCheckDialogComponent, buttons, info, Soho.Locale.translate('UnsavedChangesMessage'));
    return youMayNavigate.pipe(filter(el => el)).toPromise();
  }

  // No-oping in case this gets called on something other than a form or list.
  displayNavigationConfirmation(): Promise<any> | boolean {
    return false;
  }

  // This is used to ask the user if they want to load changes that are
  // pending, the changes we found when loading the form.
  public displayPendingChangesExist(): Promise<boolean> {
    const youMayNavigateSource = new BehaviorSubject<boolean>(false);
    const youMayNavigate: Observable<boolean> = youMayNavigateSource.asObservable();
    const info: LMDialogInfo = { name: Soho.Locale.translate('DocumentHasPendingChangesMessage') };
    // TODO: dialog message text needs work to differentiate between the Leave/Stay dialog

    const dialogId = 'PendingChanges';
    const buttons: SohoModalButton[] = [
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.APPLY ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.APPLY),
        click: (e, modal) => {
          youMayNavigateSource.next(true);
          youMayNavigateSource.complete();
          modal.close();
        },
      },
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.DISCARDCHANGES ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.DISCARDCHANGES),
        click: (e, modal) => {
          youMayNavigateSource.next(false);
          youMayNavigateSource.complete();
          modal.close();
        },
      }
    ];

    this.displayUnsavedChangesDialog(LMPendingChangesDialogComponent, buttons, info, Soho.Locale.translate('UnsavedChangesMessage'));
    return youMayNavigate.pipe(filter(el => el)).toPromise();
  }

  public displayUnsavedChangesDialog(component: ComponentType<any>, buttons: SohoModalButton[], info: LMDialogInfo, title: string): void {
    const attributes: SohoAttributesArray = [ {
      name: 'data-automation-id',
      value: `lm-unsaved-changes-modal`
    } ];

    const options: SohoModalOptions = {
      fullsize: 'responsive',
      showCloseBtn: true,
      attributes
    };

    this.dialog = this.sohoModalDialogService
      .modal(component, null, options)
      .buttons(buttons)
      .title(title)
      .beforeOpen(() => {
        this.lmBusyIndicatorService.hideBusyIndicator();
        return true;
      })
      .apply((c) => {
        c.info = info;
      })
      .open();
  }

  showBusyIndicator() {
    this.lmBusyIndicatorService.showBusyIndicator();
  }

  hideBusyIndicator() {
    this.lmBusyIndicatorService.hideBusyIndicator();
  }

  checkSaveRequired(): boolean {
    return false;
  }

  getStatus(opts: { isDirty?: boolean, hasError?: boolean }): Array<string> {
    const status = new Array<string>();

    // using an array as it affords more flexibility if we want to highlight certain states
    if (opts.isDirty !== undefined) {
      if (opts.isDirty) {
        status.push('dirty');
      } else {
        status.push('pristine');
      }
    }

    if (opts.hasError !== undefined) {
      if (opts.hasError) {
        status.push('invalid');
      } else {
        status.push('valid');
      }
    }

    return status;
  }

  protected get3_5Url(lmHtmlPageUtilService: LMHtmlPageUtilService): string {
    const processUrl: string = document.location.href;
    // if (this.isListSpec(spec)) {
    //   let listSpec: LMListSpec = <LMListSpec>spec;
    //   if (listSpec.listType === 'dashboard') {
    //     const compiledTemplateNavUrl = urltemplate.parse(listSpec.initialDataUrl);
    //     const navigationUrl = compiledTemplateNavUrl.expand({
    //       resourceId:     listSpec.resourceId,
    //       navigationName: listSpec
    //     });
    //     processUrl = navigationUrl;
    //   }
    // }

    return lmHtmlPageUtilService.getHtmlPage(processUrl);
  }

  protected setLegacyVariables(
    has4_0UnSupported: boolean,
    appDisplay: string,
    lmContextService: LMContextService,
    lmHtmlPageUtilService: LMHtmlPageUtilService,
    forceXI?: boolean
  ) {
    const showUsing = lmContextService.getCurrentWebAppSpec().showUsing;
    const showUsingXi = lmContextService.isXiFull() || lmContextService.getContext().showusingxi;

    this.legacyWebUI = (showUsing !== 'SoHo_Xi');

    if (forceXI) {
      this.legacyWebUI = false;
      this.unsupportedPattern = false;
      this.url3_5 = undefined;
    } else if (showUsingXi && !has4_0UnSupported) {
      this.legacyWebUI = false;
      this.unsupportedPattern = false;
      this.url3_5 = undefined;
    } else if (this.uiCompare) {
      this.url3_5 = this.get3_5Url(lmHtmlPageUtilService);
      this.unsupportedPattern = false;
      this.legacyWebUI = true;
    } else if (has4_0UnSupported && (appDisplay !== '3_5')) {
      this.unsupportedPattern = true;
      this.legacyWebUI = true;
      this.url3_5 = this.get3_5Url(lmHtmlPageUtilService);
    } else if (appDisplay === '4_0' || appDisplay === 'override') {
      this.legacyWebUI = false;
      this.url3_5 = undefined;
    } else if (appDisplay === '3_5') {
      this.legacyWebUI = true;
      this.url3_5 = this.get3_5Url(lmHtmlPageUtilService);
    } else if (this.legacyWebUI) {
      this.legacyWebUI = true;
      this.url3_5 = this.get3_5Url(lmHtmlPageUtilService);
    } else {
      this.legacyWebUI = false;
      this.url3_5 = undefined;
    }
  }

  isPageSpec(arg: any): arg is LMPageSpec {
    return arg && arg.pageSpec;
  }

  isListSpec(arg: any): arg is LMListSpec {
    return arg && arg.busListSpec;
  }

  isFormSpec(arg: any): arg is LMFormSpec {
    return arg && arg.busFormSpec;
  }

  protected doesSpecContainUnsupported4_0Patterns(screenSpec: LMScreenSpec): boolean {
    const retVal = false;
    if (this.isPageSpec(screenSpec)) {
      const pageSpec: LMPageSpec = <LMPageSpec>screenSpec;
      if (pageSpec.resourceId === 'LandingPageWebappPage') {
        return true;
      }
      const panels = pageSpec.pageSpec.panels;

      return this.processPanels4_0ForUnsupported(panels);
    }
    if (this.isListSpec(screenSpec)) {
      const listSpec: LMListSpec = <LMListSpec>screenSpec;
      return this.processList4_0ForUnsupported(listSpec);
    }
    if (this.isFormSpec(screenSpec)) {
      const formSpec: LMFormSpec = <LMFormSpec>screenSpec;
      return this.processForm4_0ForUnsupported(formSpec);
    }
    return false;
  }

  private processList4_0ForUnsupported(listSpec): boolean {
    const charts = listSpec.busListSpec.charts;
    if (charts) {
      const keys = Object.keys(listSpec.busListSpec.charts);
      for (let i = 0; i < keys.length; i++) {
        if (listSpec.busListSpec.charts[ keys[ i ] ].chartType === 'quadrant') {
          return true;
        }
      }
      const listResource = new ListResource({ resourceId: listSpec.resourceId });
      if (listResource.chartName) {
        if (charts[ listResource.chartName ] && charts[ listResource.chartName ].chartType === 'quadrant') {  // charts[chartName].chartType === 'gap' ||
          return true;
        }
      }
    }
    const detailSections = listSpec.busListSpec.detailSections;
    if (detailSections) {
      const detailSection = listSpec.busListSpec.detailSections;
      for (let i = 0; i < detailSections.length; i++) {
        if (detailSections[ i ].listSpec) {
          const foundUnsupported: boolean = this.processList4_0ForUnsupported(detailSections[ i ].listSpec);
          if (foundUnsupported) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private processForm4_0ForUnsupported(formSpec: LMFormSpec): boolean {
    // if (formSpec.busFormSpec.formType === LMFormType.MATRIX) {
    //   return true;
    // }
    if (formSpec.busFormSpec.formType === LMFormType.COMPOSITE) {
      const panels = formSpec.busFormSpec.panels;
      return this.processPanels4_0ForUnsupported(panels);
    }
    return false;
  }

  public instanceOfLMPagePanelSpec(object: any): object is LMPagePanelSpec {
    return 'id' in object && 'webAppSpecName' in object;
  }

  public instanceOfLMCompositePanelSpec(object: any): object is LMCompositePanelSpec {
    return 'panelId' in object;
  }

  private processPanels4_0ForUnsupported(panelSpecs: Array<LMCompositePanelSpec> | Array<LMPagePanelSpec>): boolean {
    for (let i = 0; i < panelSpecs.length; i++) {
      const panelSpecIn = panelSpecs[ i ];
      if (this.instanceOfLMPagePanelSpec(panelSpecIn)) {
        const panelSpec: LMPagePanelSpec = panelSpecIn;
        if (panelSpec.pane && panelSpec.pane.listSpec) {
          return this.processList4_0ForUnsupported(panelSpec.pane.listSpec);
        }
      } else if (this.instanceOfLMCompositePanelSpec(panelSpecIn)) {
        const panelSpec: LMCompositePanelSpec = panelSpecIn;
        if (panelSpec.panelType === 'formlist') {
          const lSpec: LMListSpec = panelSpec.listSpec;
          if (lSpec) {
            if (this.processList4_0ForUnsupported(lSpec)) {
              return true;
            }
          }
        } else if (panelSpec.panelType === 'form') {

        }
      }
    }
  }

  public get componentDestroyed(): boolean {
    return this.m_componentDestroyed;
  }

  protected setComponentDestroyed() {
    this.m_componentDestroyed = true;
  }

  public setUserWebRequestId(id: string) {
    this.userWebRequestId = id;
  }
}

export class LMRefreshRequest {
  public constructor(
    public originatingPanelSourceId?: string,
    public originatingSourceId?: string
  ) {
  }
}
